<script setup lang="ts">
import { ref } from "vue";
import { trackPageView } from "@/analytics/app-analytics";
import { Logger } from "@/logger";
import { TUTORIALS_URL } from "@/utils/constants";
import FeedbackModal from "@/components/UI/FeedbackModal.vue";
import ChatbotPanel from "@/components/chatbot/ChatbotPanel.vue";
import Offcanvas from "bootstrap/js/dist/offcanvas";

const showFeedbackModal = ref(false);
const isChatOpen = ref(false);

function openHelp() {
  Logger.info(`OffcanvasHelp.vue : open help`);
  // Counted with the page views: a header panel is a "page" to the user.
  trackPageView("panel-help");
}

function openFeedback() {
  const offcanvasEl = document.getElementById("offcanvasHelp");
  if (offcanvasEl) {
    Offcanvas.getInstance(offcanvasEl)?.hide();
  }
  showFeedbackModal.value = true;
}

function onChatOpenChange(value: boolean) {
  isChatOpen.value = value;
}
</script>
<template>
  <a
    data-bs-toggle="offcanvas"
    href="#offcanvasHelp"
    @click="openHelp()"
    role="button"
    aria-controls="offcanvasHelp"
    class="dr-tooltip"
    :data-tooltip="$t('navbar.helpTooltip')"
    :aria-label="$t('navbar.helpTooltip')"
  >
    <el-icon size="18" class="text-dark">
      <i class="bi bi-question-circle"></i>
    </el-icon>
  </a>
  <div
    class="offcanvas offcanvas-end"
    tabindex="-1"
    id="offcanvasHelp"
    aria-labelledby="offcanvasHelpLabel"
  >
    <div v-if="!isChatOpen" class="offcanvas-header bg-white">
      <h4 class="offcanvas-title" id="offcanvasHelpLabel">
        {{ $t("help.title") }}
      </h4>
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      ></button>
    </div>
    <div
      class="offcanvas-body d-flex flex-column"
      :class="isChatOpen ? 'p-0 overflow-hidden' : 'bg-white'"
    >
      <template v-if="!isChatOpen">
        <h5>
          <strong>{{ $t("help.contextualHelp") }}</strong>
        </h5>
        <p>
          {{ $t("help.contextualHelpDescription") }}
        </p>
        <h5>
          <strong>{{ $t("help.tutorialSection") }}</strong>
        </h5>
        <p>
          <a :href="TUTORIALS_URL" target="_blank" rel="noopener noreferrer"
            >{{ $t("help.tutorialsLink") }}
          </a>
          <br />

          {{ $t("help.tutorialsDescription") }}
        </p>
      </template>

      <ChatbotPanel @update:chat-open="onChatOpenChange" />

      <!-- Feedback Banner - right below chatbot banner -->
      <div v-if="!isChatOpen" class="feedback-banner" @click="openFeedback">
        <div class="feedback-banner-icon">
          <i class="bi-chat-heart"></i>
        </div>
        <div class="feedback-banner-content">
          <div class="feedback-banner-title">
            {{ $t("feedback.bot.banner.title") }}
          </div>
          <div class="feedback-banner-description">
            {{ $t("feedback.bot.banner.description") }}
          </div>
        </div>
        <i class="bi-chevron-right feedback-banner-arrow"></i>
      </div>
    </div>
  </div>

  <FeedbackModal v-model="showFeedbackModal" view="Dashboard" />
</template>

<style lang="scss" scoped>
.feedback-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  margin-top: 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.feedback-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #f59e0b;
  border-radius: 10px;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
}

.feedback-banner-content {
  flex: 1;
  min-width: 0;
}

.feedback-banner-title {
  font-weight: 600;
  font-size: 14px;
  color: #92400e;
  margin-bottom: 2px;
}

.feedback-banner-description {
  font-size: 12px;
  color: #b45309;
}

.feedback-banner-arrow {
  color: #92400e;
  font-size: 14px;
  flex-shrink: 0;
}
</style>
